import { apiFetch, API_BASE_URL } from './api.service';

/** Формат содержимого секции для LLM (задача TPL1, по образцу meetily templates). */
export type ProtocolTemplateSectionFormat = 'paragraph' | 'list' | 'string';

/**
 * Одна секция шаблона протокола: ключ секции, заголовок и подсказка для AI-генерации.
 *
 * `hint` — старое (P3a) поле-подсказка, остаётся для обратной совместимости.
 * `instruction`/`format`/`item_format` — расширенная схема (задача TPL1): детальная
 * инструкция, ожидаемый формат содержимого и (для списков) формат одного элемента.
 * Все новые поля опциональны — старые шаблоны {key, title, hint} читаются как есть.
 */
export type ProtocolTemplateSection = {
  key: string;
  title: string;
  hint?: string;
  instruction?: string;
  format?: ProtocolTemplateSectionFormat;
  item_format?: string;
};

/** Шаблон протокола совещания (библиотека шаблонов, задача P3a/P3b на бэке). */
export type ProtocolTemplate = {
  id: number;
  name: string;
  description?: string | null;
  sections: ProtocolTemplateSection[];
  is_default: boolean;
  is_global: boolean;
  owner_id?: number | null;
};

export type ProtocolTemplatePayload = {
  name: string;
  description?: string;
  sections: ProtocolTemplateSection[];
};

function parseApiError(text: string, fallback: string): string {
  try {
    const parsed = JSON.parse(text) as { detail?: string | { msg?: string }[] };
    if (typeof parsed.detail === 'string' && parsed.detail) {
      return parsed.detail;
    }
    if (Array.isArray(parsed.detail)) {
      const msg = parsed.detail.map((item) => item.msg || '').filter(Boolean).join('. ');
      if (msg) return msg;
    }
  } catch {
    // not JSON
  }
  return text || fallback;
}

async function handleJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseApiError(text, fallback));
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

export const templatesService = {
  async list(): Promise<ProtocolTemplate[]> {
    const res = await apiFetch(`${API_BASE_URL}/api/templates`);
    return handleJson<ProtocolTemplate[]>(res, 'Не удалось загрузить шаблоны');
  },

  /** Требует роль менеджер/админ на бэке. */
  async create(payload: ProtocolTemplatePayload): Promise<ProtocolTemplate> {
    const res = await apiFetch(`${API_BASE_URL}/api/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleJson<ProtocolTemplate>(res, 'Не удалось создать шаблон');
  },

  /** Требует роль менеджер/админ на бэке. */
  async update(id: number, payload: ProtocolTemplatePayload): Promise<ProtocolTemplate> {
    const res = await apiFetch(`${API_BASE_URL}/api/templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return handleJson<ProtocolTemplate>(res, 'Не удалось сохранить шаблон');
  },

  /** Нельзя удалить шаблон с is_default: true — бэк вернёт 400 с пояснением. */
  async remove(id: number): Promise<void> {
    const res = await apiFetch(`${API_BASE_URL}/api/templates/${id}`, { method: 'DELETE' });
    await handleJson<void>(res, 'Не удалось удалить шаблон');
  },

  /** multipart/form-data, поле файла — 'file'; без ручного Content-Type (boundary подставит браузер). */
  async importDocx(file: File): Promise<ProtocolTemplate> {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiFetch(`${API_BASE_URL}/api/templates/import-docx`, {
      method: 'POST',
      body: formData,
    });
    return handleJson<ProtocolTemplate>(res, 'Не удалось импортировать шаблон из DOCX');
  },
};
